import axios from 'axios';
import { authHeader } from './auth';

const BASE = process.env.REACT_APP_NHIS_API_URL
  ? String(process.env.REACT_APP_NHIS_API_URL).replace(/\/$/, '')
  : String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// NHIS 응답에서 에러 유형을 분류한다.
// raw 서버 메시지(Coocon 등 내부 메시지)는 사용자에게 노출하지 않는다.
// HTTP 상태코드와 에러 타입만으로 사용자 친화적 문구를 결정한다.
export function parseNhisError(err) {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ECONNABORTED') {
      return { type: 'timeout', message: '요청 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.' };
    }
    if (!err.response) {
      return { type: 'network', message: '네트워크 연결을 확인하고 다시 시도해주세요.' };
    }
    const status = err.response.status;
    if (status === 401) {
      return { type: 'auth', message: '인증이 만료되었습니다. 다시 로그인해주세요.' };
    }
    if (status === 502 || status === 503 || status === 504) {
      return { type: 'server', message: '서비스에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.' };
    }
    if (status >= 500) {
      return { type: 'server', message: '서비스에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.' };
    }
    // success: false 포함 4xx — 호출 측에서 문맥에 맞는 메시지를 오버라이드해서 사용한다.
    return { type: 'fail', message: '요청을 처리할 수 없습니다. 다시 시도해주세요.' };
  }
  if (err.name === 'CanceledError' || err.name === 'AbortError') {
    return { type: 'aborted', message: '' };
  }
  return { type: 'unknown', message: '알 수 없는 오류가 발생했습니다. 다시 시도해주세요.' };
}

// success 필드를 확인하고, false 이면 Error를 던진다.
function assertSuccess(data) {
  if (!data || data.success !== true) {
    const err = new Error(data?.message || '요청이 실패했습니다.');
    err.nhisCode = data?.code;
    throw err;
  }
}

/**
 * POST /api/nhis/simple-auth/login
 * @param {{ loginMethod: string, name: string, phoneNumber: string, birthDate: string }} params
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<{ session: object, steps: object[] }>}
 */
export async function nhisLogin({ loginMethod, name, phoneNumber, birthDate }, { signal } = {}) {
  const res = await axios.post(
    `${BASE}/api/nhis/simple-auth/login`,
    { loginMethod, name, phoneNumber, birthDate },
    { signal },
  );
  assertSuccess(res.data);
  return res.data.data;
}

/**
 * POST /api/nhis/simple-auth/verify
 * @param {{ session: object }} params
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<{ session: object, message: string, stdPatientId?: number }>}
 */
export async function nhisVerify({ session }, { signal } = {}) {
  const res = await axios.post(
    `${BASE}/api/nhis/simple-auth/verify`,
    { session },
    { signal },
  );
  assertSuccess(res.data);
  return res.data.data;
}

/**
 * POST /api/nhis/collect-all
 * 대용량 응답 대비 timeout: 600,000ms
 * @param {{ session: object, stdPatientId: number }} params
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<{ session: object, healthCheckup: object, medicalHistory: object, treatmentMedication: object }>}
 */
export async function nhisCollectAll({ session, stdPatientId }, { signal } = {}) {
  const parsedStdPatientId = Number(stdPatientId);
  if (!Number.isInteger(parsedStdPatientId) || parsedStdPatientId <= 0) {
    throw new Error('유효한 stdPatientId가 필요합니다.');
  }

  const res = await axios.post(
    `${BASE}/api/nhis/collect-all`,
    { session, stdPatientId: parsedStdPatientId },
    { timeout: 600_000, signal },
  );
  assertSuccess(res.data);
  return res.data.data;
}

/**
 * POST /api/nhis/collect-all/jobs
 * NHIS 수집 Job 생성. 실제 Coocon/NHIS 수집은 backend에서 비동기 Job으로 실행된다.
 * @param {{ session: object, stdPatientId: number }} params
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<{ jobId: string, status: string, currentStep?: string }>}
 */
export async function nhisStartCollectAllJob({ session, stdPatientId }, { signal } = {}) {
  const parsedStdPatientId = Number(stdPatientId);
  if (!Number.isInteger(parsedStdPatientId) || parsedStdPatientId <= 0) {
    throw new Error('유효한 stdPatientId가 필요합니다.');
  }

  const res = await axios.post(
    `${BASE}/api/nhis/collect-all/jobs`,
    { session, stdPatientId: parsedStdPatientId },
    { timeout: 15_000, signal },
  );
  assertSuccess(res.data);
  return res.data.data;
}

/**
 * GET /api/nhis/collect-all/jobs/{jobId}
 * NHIS 수집 Job 상태 조회.
 * @param {string} jobId
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<{
 *   jobId: string,
 *   status: string,
 *   currentStep?: string,
 *   progressPercent?: number,
 *   errorCode?: string,
 *   errorMessage?: string,
 *   startedAt?: string,
 *   completedAt?: string,
 *   updatedAt?: string,
 * }>}
 */
export async function nhisGetCollectAllJob(jobId, { signal } = {}) {
  const normalizedJobId = String(jobId || '').trim();
  if (!normalizedJobId) {
    throw new Error('유효한 jobId가 필요합니다.');
  }

  const res = await axios.get(
    `${BASE}/api/nhis/collect-all/jobs/${encodeURIComponent(normalizedJobId)}`,
    { timeout: 15_000, signal },
  );
  assertSuccess(res.data);
  return res.data.data;
}

/**
 * GET /api/nhis/collect-all/latest
 * 현재 로그인한 사용자의 마이데이터 수집 상태 조회
 * @returns {Promise<{ patients: Array<{ stdPatientId: number, hasCollection: boolean, lastCollectedAt?: string }> }>}
 */
export async function nhisGetLatestCollection() {
  const res = await axios.get(
    `${BASE}/api/nhis/collect-all/latest`,
    { headers: authHeader() },
  );
  assertSuccess(res.data);
  return res.data.data;
}

/**
 * POST /api/nhis/simple-auth/login/me
 * 앱 전용: JWT에서 사용자 정보 자동 추출, loginMethod만 전달
 * @param {{ loginMethod: string }} params
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<{ session: object, steps: object[] }>}
 */
export async function nhisLoginMe({ loginMethod }, { signal } = {}) {
  const res = await axios.post(
    `${BASE}/api/nhis/simple-auth/login/me`,
    { loginMethod },
    { headers: authHeader(), signal },
  );
  assertSuccess(res.data);
  return res.data.data;
}

/**
 * POST /api/nhis/simple-auth/logout  (best-effort)
 * 실패해도 UX에 영향 없음. 에러를 외부로 전파하지 않는다.
 * @param {{ session: object, stdPatientId: number }} params
 */
export async function nhisLogout({ session, stdPatientId }) {
  try {
    await axios.post(
      `${BASE}/api/nhis/simple-auth/logout`,
      { session, stdPatientId },
      { timeout: 5_000 },
    );
  } catch {
    // 세션이 이미 만료되어도 무관하므로 에러를 무시한다.
  }
}
